import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import React from "react";
import { ConfirmDialogOptions } from "./confirm-dialog.component";

interface ConfirmDialogState {
  open: boolean;
  title: string;
  content: string | React.ReactNode;
  options: ConfirmDialogOptions | undefined;
  onChange: (() => void) | undefined;
  onCancel: (() => void) | undefined;
}

// Define the initial state using that type
const initialState: ConfirmDialogState = {
  open: false,
  title: "",
  content: "",
  options: undefined,
  onChange: undefined,
  onCancel: undefined,
};

export const confirmDialogSlice = createSlice({
  name: "confirmDialog",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    setContent: (state, action: PayloadAction<any>) => {
      state.content = action.payload;
    },
    setOptions: (state, action: PayloadAction<any>) => {
      state.options = action.payload;
    },
    setOnChange: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.onChange = action.payload;
    },
    setOnCancel: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.onCancel = action.payload;
    },
    resetConfirmDialog: (
      state,
      action: PayloadAction<Partial<ConfirmDialogState> | undefined>
    ) => ({
      ...initialState,
      ...action.payload,
    }),
  },
});

// Action creators are generated for each case reducer function
export const {
  setOpen,
  setTitle,
  setContent,
  setOnChange,
  setOnCancel,
  setOptions,
  resetConfirmDialog,
} = confirmDialogSlice.actions;

const confirmDialogSelector = (state: RootState) => state.confirmDialog;
export const selectConfirmDialogOpen = createSelector(
  confirmDialogSelector,
  (confirmDialog) => confirmDialog.open
);
export const selectConfirmDialogTitle = createSelector(
  confirmDialogSelector,
  (confirmDialog) => confirmDialog.title
);
export const selectConfirmDialogContent = createSelector(
  confirmDialogSelector,
  (confirmDialog) => confirmDialog.content
);
export const selectConfirmDialogOptions = createSelector(
  confirmDialogSelector,
  (confirmDialog) => confirmDialog.options
);
export const selectConfirmDialogOnChange = createSelector(
  confirmDialogSelector,
  (confirmDialog) => confirmDialog.onChange
);
export const selectConfirmDialogOnCancel = createSelector(
  confirmDialogSelector,
  (confirmDialog) => confirmDialog.onCancel
);

export default confirmDialogSlice.reducer;
